import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, Users, CheckSquare, StickyNote, FolderOpen, Target, Activity, PieChart } from 'lucide-react';
import { useToast } from '../../../hooks/use-toast';
import BackButton from '../../../components/admin/BackButton';
import DashboardSection from '../../../components/admin/crm/DashboardSection';
import KpiCard from '../../../components/admin/crm/KpiCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CRMDashboard = () => {
  const [kpis, setKpis] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [activities, setActivities] = useState(null);
  const [leadsBreakdown, setLeadsBreakdown] = useState(null);
  const [tasksStatus, setTasksStatus] = useState(null);
  const [documentsOverview, setDocumentsOverview] = useState(null);
  const [loading, setLoading] = useState({ kpis: true });
  const { toast } = useToast();

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    fetchKPIs();
  }, []);

  const fetchKPIs = async () => {
    try {
      setLoading(prev => ({ ...prev, kpis: true }));
      const response = await fetch(`${API_URL}/api/crm/dashboard/kpis`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch KPIs');

      const data = await response.json();
      setKpis(data);
    } catch (error) {
      console.error('Error fetching KPIs:', error);
      toast({
        title: 'Error',
        description: 'Failed to load dashboard KPIs',
        variant: 'destructive'
      });
    } finally {
      setLoading(prev => ({ ...prev, kpis: false }));
    }
  };

  const fetchSalesFunnel = async () => {
    if (funnel) return; // Already loaded

    try {
      setLoading(prev => ({ ...prev, funnel: true }));
      const response = await fetch(`${API_URL}/api/crm/dashboard/funnel`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch funnel');

      const data = await response.json();
      setFunnel(data.funnel);
    } catch (error) {
      console.error('Error fetching funnel:', error);
    } finally {
      setLoading(prev => ({ ...prev, funnel: false }));
    }
  };

  const fetchActivities = async () => {
    if (activities) return; // Already loaded

    try {
      setLoading(prev => ({ ...prev, activities: true }));
      const response = await fetch(`${API_URL}/api/crm/dashboard/activities?days=7`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch activities');

      const data = await response.json();
      setActivities(data.activities);
    } catch (error) {
      console.error('Error fetching activities:', error);
    } finally {
      setLoading(prev => ({ ...prev, activities: false }));
    }
  };

  const fetchLeadsBreakdown = async () => {
    if (leadsBreakdown) return; // Already loaded

    try {
      setLoading(prev => ({ ...prev, leads: true }));
      const response = await fetch(`${API_URL}/api/crm/dashboard/leads`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch leads');

      const data = await response.json();
      setLeadsBreakdown(data.breakdown);
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLoading(prev => ({ ...prev, leads: false }));
    }
  };

  const fetchTasksStatus = async () => {
    if (tasksStatus) return; // Already loaded

    try {
      setLoading(prev => ({ ...prev, tasks: true }));
      const response = await fetch(`${API_URL}/api/crm/dashboard/tasks`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch tasks');

      const data = await response.json();
      setTasksStatus(data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(prev => ({ ...prev, tasks: false }));
    }
  };

  const fetchDocumentsOverview = async () => {
    if (documentsOverview) return; // Already loaded

    try {
      setLoading(prev => ({ ...prev, documents: true }));
      const response = await fetch(`${API_URL}/api/crm/dashboard/documents`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch documents');

      const data = await response.json();
      setDocumentsOverview(data);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(prev => ({ ...prev, documents: false }));
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const formatStage = (stage) => {
    return stage.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  return (
    <div>
      <BackButton />

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-3">
          <div className="p-3 bg-[#00B4D8] bg-opacity-10 rounded-lg">
            <BarChart3 className="h-6 w-6 text-[#00B4D8]" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-[#2B2D42]">CRM Dashboard</h2>
            <p className="text-sm text-[#6C757D]">Analytics and insights for your CRM system</p>
          </div>
        </div>
      </div>

      {/* Section 1: Overview KPIs (Default Expanded) */}
      <DashboardSection
        title="Overview KPIs"
        icon={Activity}
        defaultExpanded={true}
      >
        {loading.kpis ? (
          <p className="text-center text-[#6C757D]">Loading KPIs...</p>
        ) : kpis ? (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <KpiCard 
              title="Total Clients" 
              value={kpis.total_clients} 
              icon={Users} 
              color="#00B4D8"
            />
            <KpiCard 
              title="Total Leads" 
              value={kpis.total_leads} 
              icon={Target} 
              color="#10B981"
            />
            <KpiCard 
              title="Pipeline Value" 
              value={formatCurrency(kpis.pipeline_value)} 
              icon={TrendingUp} 
              color="#8B5CF6"
            />
            <KpiCard 
              title="Conversion Rate" 
              value={`${kpis.conversion_rate}%`} 
              icon={BarChart3} 
              color="#F59E0B"
            />
            <KpiCard 
              title="Total Tasks" 
              value={kpis.total_tasks} 
              icon={CheckSquare} 
              color="#3B82F6"
              subtitle={`${kpis.pending_tasks} pending`}
            />
            <KpiCard 
              title="Total Notes" 
              value={kpis.total_notes} 
              icon={StickyNote} 
              color="#00B4D8"
            />
            <KpiCard 
              title="Total Documents" 
              value={kpis.total_documents} 
              icon={FolderOpen} 
              color="#6366F1"
            />
            <KpiCard 
              title="Activities This Week" 
              value={kpis.activities_week} 
              icon={Activity} 
              color="#EF4444"
              subtitle={`${kpis.activities_today} today`}
            />
          </div>
        ) : (
          <p className="text-center text-[#6C757D]">Failed to load KPIs</p>
        )}
      </DashboardSection>

      {/* Section 2: Sales Funnel */}
      <DashboardSection
        title="Sales Funnel"
        icon={TrendingUp}
        defaultExpanded={false}
        onExpand={fetchSalesFunnel}
      >
        {loading.funnel ? (
          <p className="text-center text-[#6C757D]">Loading funnel...</p>
        ) : funnel && funnel.length > 0 ? (
          <div className="space-y-3">
            {funnel.map((stage, index) => {
              const percentage = funnel.length > 0 ? (stage.count / Math.max(...funnel.map(s => s.count))) * 100 : 0;
              return (
                <div key={stage.stage} className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="font-medium text-[#2B2D42]">{formatStage(stage.stage)}</span>
                    <span className="text-[#6C757D]">
                      {stage.count} opportunities • {formatCurrency(stage.value)}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className="bg-[#00B4D8] h-3 rounded-full transition-all duration-500"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-center text-[#6C757D]">No funnel data available</p>
        )}
      </DashboardSection>

      {/* Section 3: Activities Insights */}
      <DashboardSection
        title="Activities Insights (Last 7 Days)"
        icon={Activity}
        defaultExpanded={false}
        onExpand={fetchActivities}
      >
        {loading.activities ? (
          <p className="text-center text-[#6C757D]">Loading activities...</p>
        ) : activities && activities.length > 0 ? (
          <div className="space-y-2">
            {activities.map((day) => (
              <div key={day._id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="font-medium text-[#2B2D42]">{day._id}</span>
                <div className="flex space-x-4 text-sm">
                  <span className="text-[#00B4D8]">Notes: {day.notes}</span>
                  <span className="text-blue-600">Tasks: {day.tasks}</span>
                  <span className="text-green-600">Calls: {day.calls}</span>
                  <span className="text-purple-600">Emails: {day.emails}</span>
                  <span className="text-orange-600">Meetings: {day.meetings}</span>
                  <span className="font-bold text-[#2B2D42]">Total: {day.total}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center text-[#6C757D]">No activity data available</p>
        )}
      </DashboardSection>

      {/* Section 4: Leads Overview */}
      <DashboardSection
        title="Leads Overview"
        icon={Target}
        defaultExpanded={false}
        onExpand={fetchLeadsBreakdown}
      >
        {loading.leads ? (
          <p className="text-center text-[#6C757D]">Loading leads...</p>
        ) : leadsBreakdown ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {Object.entries(leadsBreakdown).map(([status, count]) => (
              <div key={status} className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-[#6C757D] mb-1">{formatStage(status)}</p>
                <p className="text-2xl font-bold text-[#00B4D8]">{count}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center text-[#6C757D]">No leads data available</p>
        )}
      </DashboardSection>

      {/* Section 5: Tasks Status */}
      <DashboardSection
        title="Tasks Status"
        icon={PieChart}
        defaultExpanded={false}
        onExpand={fetchTasksStatus}
      >
        {loading.tasks ? (
          <p className="text-center text-[#6C757D]">Loading tasks...</p>
        ) : tasksStatus ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard title="Total" value={tasksStatus.total} color="#2B2D42" />
            <KpiCard title="Completed" value={tasksStatus.completed} color="#10B981" />
            <KpiCard title="Pending" value={tasksStatus.pending} color="#F59E0B" />
            <KpiCard title="Overdue" value={tasksStatus.overdue} color="#EF4444" />
          </div>
        ) : (
          <p className="text-center text-[#6C757D]">No tasks data available</p>
        )}
      </DashboardSection>

      {/* Section 6: Documents Overview */}
      <DashboardSection
        title="Documents Overview"
        icon={FolderOpen}
        defaultExpanded={false}
        onExpand={fetchDocumentsOverview}
      >
        {loading.documents ? (
          <p className="text-center text-[#6C757D]">Loading documents...</p>
        ) : documentsOverview ? (
          <div className="space-y-4">
            {/* By Category */}
            {documentsOverview.by_category && documentsOverview.by_category.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-[#2B2D42] mb-2">By Category</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {documentsOverview.by_category.map((cat) => (
                    <div key={cat.category} className="text-center p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-[#6C757D] mb-1">{formatStage(cat.category)}</p>
                      <p className="text-xl font-bold text-[#00B4D8]">{cat.count}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Recent Uploads */}
            {documentsOverview.recent_uploads && documentsOverview.recent_uploads.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-[#2B2D42] mb-2">Recent Uploads</h4>
                <div className="space-y-2">
                  {documentsOverview.recent_uploads.map((doc) => (
                    <div key={doc.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-[#2B2D42]">{doc.filename}</p>
                        <p className="text-xs text-[#6C757D]">
                          {formatStage(doc.category)} • {doc.entity_type}
                        </p>
                      </div>
                      <p className="text-xs text-[#6C757D]">
                        {new Date(doc.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <p className="text-center text-[#6C757D]">No documents data available</p>
        )}
      </DashboardSection>
    </div>
  );
};

export default CRMDashboard;
